import { COMPANY, IMAGES, DIFFERENCE } from "../lib/data";
import { PageHero, SectionHead, CTABand } from "../components/site/Bits";
import { Reveal } from "../components/site/Reveal";
import { Link } from "react-router-dom";
import { Handshake, Radio, Wrench, HeartHandshake, Sprout, ArrowRight } from "lucide-react";

const VALUES = [
  { icon: Handshake, title: "Relationships", desc: "We invest in long-term partnerships, not transactions." },
  { icon: Radio, title: "Responsiveness", desc: "When you call, we're ready to assist." },
  { icon: Wrench, title: "Technical Expertise", desc: "Product knowledge applied to your real-world risks." },
  { icon: HeartHandshake, title: "Personalized Service", desc: "Solutions built around your fleet, sites, and requirements." },
];

import { Seo } from "../components/site/Seo";

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About" description="OneHarvest was built for operators who need a partner, not a distributor — specialized industrial products, guidance, and jobsite support backed by real field expertise." />
      <PageHero
        kicker="About OneHarvest"
        title="The operators we serve need a partner, not a distributor."
        subtitle="OneHarvest was created because equipping operators with the right products takes more than a catalog. It takes people who understand your operation and care about your success."
        image={IMAGES.aboutHero}
      />

      <section className="bg-background">
        <div className="container-x py-24 grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <SectionHead kicker="Our Story" title="Built for the field, backed by expertise." />
            <div className="mt-6 space-y-5 text-muted-foreground text-lg leading-relaxed">
              <p>
                We started OneHarvest because we saw the same problem again and again: contractors being sold products by companies that never set foot on their jobsite, never understood their fleet, and disappeared the moment something went wrong.
              </p>
              <p>
                They deserve more than a distributor. They deserve a trusted partner who understands their operations and helps them get the right products in place <em>before</em> they need them.
              </p>
              <p>
                So we built the opposite of a catalog company — a specialized industrial solutions partner that focuses on a carefully curated portfolio of best-in-class products, organized by sector and backed by exceptional service, technical expertise, and customized solutions.
              </p>
              <p className="text-ink font-medium">
                Our business is built on relationships, responsiveness, technical expertise, and personalized service. As we grow, our portfolio grows with it — but our curated, advisor-first approach never changes.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <img src={IMAGES.pipeline} alt="Pipeline operations" className="w-full h-80 object-cover border border-border" />
            <div className="mt-4 border border-border bg-card p-8">
              <div className="kicker text-flame">Our Promise</div>
              <p className="mt-3 font-display font-bold text-xl text-ink leading-snug">"{COMPANY.philosophy}"</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-charcoal">
        <div className="container-x py-24">
          <SectionHead dark center kicker="What We Stand For" title="Four principles guide every engagement" />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="group relative bg-ink border border-white/10 p-7 h-full overflow-hidden hover:border-flame/50 transition-colors">
                  <div className="absolute top-0 left-0 h-1 w-0 bg-flame group-hover:w-full transition-all duration-500 ease-out" />
                  <div className="flex items-center justify-between">
                    <div className="h-12 w-12 flex items-center justify-center bg-flame/10 border border-flame/25 text-flame group-hover:bg-flame group-hover:text-white transition-colors duration-300">
                      <v.icon className="h-6 w-6" strokeWidth={2} />
                    </div>
                    <span className="font-mono font-bold text-3xl text-white/10 group-hover:text-flame/40 transition-colors duration-300">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="mt-6 font-display font-bold text-xl text-white">{v.title}</h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-16 flex flex-wrap justify-center gap-2.5">
            {DIFFERENCE.map((d) => (
              <span key={d} className="inline-flex items-center gap-2 border border-white/15 text-white/70 px-4 py-2 text-sm hover:border-flame/50 hover:text-white hover:bg-white/[0.03] transition-colors">
                <span className="h-1.5 w-1.5 bg-flame rounded-full flex-shrink-0" /> {d}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* STEWARDSHIP */}
      <section className="relative overflow-hidden" data-testid="about-stewardship">
        <img src={IMAGES.farmland} alt="Farmland" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/85" />
        <div className="absolute inset-0 grain opacity-10" />
        <div className="relative container-x py-24 grid gap-10 lg:grid-cols-12 items-center">
          <Reveal className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-11 w-11 flex items-center justify-center bg-flame/15 border border-flame/30 text-flame"><Sprout className="h-5 w-5" strokeWidth={2} /></div>
              <div className="kicker text-flame">Beyond The Job · Stewarding The Resource</div>
            </div>
            <h2 className="font-display font-extrabold text-white text-3xl md:text-4xl tracking-tight leading-[1.12] text-balance">
              We believe protecting the land is protecting our customers' future.
            </h2>
            <p className="mt-6 text-white/75 text-lg max-w-2xl leading-relaxed">
              Projects are near-term; land produces on cycles that span generations. The ground under today's operations becomes tomorrow's premium agriculture and livestock land. Our portfolio is built to keep that resource clean, intact, and valuable — a legacy that serves landowners and operators alike.
            </p>
            <Link to="/land-stewardship" data-testid="about-stewardship-cta" className="mt-8 inline-flex items-center gap-2 text-flame font-semibold hover:text-white transition-colors">
              Read our stewardship narrative <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
